import React, { useState, useEffect } from 'react';
import { ChevronDown, Play, Pause, Trash2, AlertCircle, Loader2 } from 'lucide-react';
import * as Switch from '@radix-ui/react-switch';
import * as Collapsible from '@radix-ui/react-collapsible';
import { getSession } from '../../lib/auth';
import {
  deleteAudioFile,
  getAudioPlaybackUrl,
  listAudioFiles,
  type AudioFile,
} from '../../lib/api';
import { audioManager } from '../../utils/audio';
import { audioCache } from '../../utils/audioCache';
import { AudioUploader } from '../Audio/AudioUploader';
import { useTimer } from '../../context/TimerContext';

interface CachedAudioFile {
  name: string;
  blob: Blob;
}

interface AudioSettingsProps {
  soundEnabled: boolean;
  onSoundEnabledChange: (enabled: boolean) => void;
}

const soundTypeMap = {
  focus: 'focus',
  shortBreak: 'shortBreak',
  longBreak: 'longBreak',
} as const;

export function AudioSettings({ soundEnabled, onSoundEnabledChange }: AudioSettingsProps) {
  const { settings, updateSettings, state, setShowUpgradeModal } = useTimer();
  const isPremium = state.isPremium;
  const [audioFiles, setAudioFiles] = useState<AudioFile[]>([]);
  const [cachedFiles, setCachedFiles] = useState<CachedAudioFile[]>([]);
  const [isManageOpen, setIsManageOpen] = useState(false);
  const [playingId, setPlayingId] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // Initialize audio context on first interaction
  const initializeAudio = async () => {
    try {
      await audioManager.ensureInitialized();
    } catch (error) {
      console.error('Error initializing audio:', error);
    }
  };

  const handleSoundToggle = async (enabled: boolean) => {
    if (enabled) {
      await initializeAudio();
    }
    onSoundEnabledChange(enabled);
  };

  const loadAudioFiles = async () => {
    const session = getSession();
    
    if (session?.user) {
      try {
        const files = await listAudioFiles();
        setAudioFiles(files);
      } catch (error) {
        console.error('Error loading audio files:', error);
      }
    } else {
      // Load from IndexedDB if not authenticated
      try {
        const keys = await audioCache.getAllKeys();
        const files: CachedAudioFile[] = [];
        
        for (const key of keys) {
          const blob = await audioCache.getBlob(key);
          if (blob) {
            files.push({ name: key, blob });
          }
        }
        
        setCachedFiles(files);
      } catch (error) {
        console.error('Error loading cached files:', error);
      }
    }
  };

  useEffect(() => {
    loadAudioFiles();
  }, []);

  useEffect(() => {
    if (settings.customSounds) {
      Object.entries(settings.customSounds).forEach(([type, path]) => {
        if (path && type in soundTypeMap) {
          audioManager.setCustomSound(type as keyof typeof soundTypeMap, path);
        }
      });
    }
  }, [settings.customSounds]);

  const handleSoundSelect = async (type: keyof typeof soundTypeMap, audioFile: AudioFile | CachedAudioFile | null) => {
    await initializeAudio();
    const customSounds = { ...settings.customSounds };
    
    if (!audioFile) {
      delete customSounds[type];
    } else {
      customSounds[type] = 'file_name' in audioFile ? audioFile.storage_path : audioFile.name;
    }

    updateSettings({ customSounds });

    if (audioFile) {
      audioManager.setCustomSound(type, 'file_name' in audioFile ? audioFile.storage_path : audioFile.name);
    } else {
      audioManager.setCustomSound(type, '');
    }
  };

  const handlePlay = async (file: AudioFile | CachedAudioFile) => {
    await initializeAudio();
    
    const fileId = 'id' in file ? file.id : file.name;
    
    if (playingId === fileId) {
      // Stop the current playback
      audioManager.stopAllSounds();
      setPlayingId(null);
      return;
    }

    try {
      // Get the audio URL
      let url: string;
      if ('id' in file) {
        const session = getSession();
        if (session?.user) {
          url = await getAudioPlaybackUrl(file.storage_path);
        } else {
          // If not authenticated, try to get from cache
          const cachedUrl = await audioCache.loadFromCache(file.storage_path);
          if (!cachedUrl) throw new Error('Failed to load from cache');
          url = cachedUrl;
        }
      } else {
        // Cached file
        const cachedUrl = await audioCache.loadFromCache(file.name);
        if (!cachedUrl) throw new Error('Failed to load from cache');
        url = cachedUrl;
      }

      setPlayingId(fileId);
      
      // Create and play the audio element
      const audio = new Audio(url);
      audio.setAttribute('playsinline', 'true');
      audio.setAttribute('webkit-playsinline', 'true');
      audio.setAttribute('x-webkit-airplay', 'allow');
      audio.volume = 1.0;
      
      // Store the audio element for stopping later
      audioManager.setPreviewAudio(audio);
      
      // Play the audio
      await audio.play();
      
      // Handle playback end
      audio.onended = () => {
        setPlayingId(null);
      };
    } catch (error) {
      console.error('Error playing audio:', error);
      setPlayingId(null);
    }
  };

  const handleDelete = async (file: AudioFile | CachedAudioFile) => {
    try {
      setIsDeleting(true);
      setDeletingId(null); // Close the confirmation dialog

      const session = getSession();
      const isAuthenticated = !!session?.user;

      if ('id' in file && isAuthenticated) {
        await deleteAudioFile(file.id);
      } else {
        // Delete from cache for non-authenticated users or cached files
        await audioCache.deleteFromCache('file_name' in file ? file.storage_path : file.name);
      }

      // Remove from custom sounds if it was selected
      const customSounds = { ...settings.customSounds };
      let updated = false;

      Object.entries(customSounds).forEach(([key, value]) => {
        if (value === ('file_name' in file ? file.storage_path : file.name)) {
          delete customSounds[key];
          updated = true;
        }
      });

      if (updated) {
        updateSettings({ customSounds });
      }

      // Reload the file list
      await loadAudioFiles();
    } catch (error) {
      console.error('Error deleting audio file:', error);
    } finally {
      setIsDeleting(false);
    }
  };

  const getSelectedFileName = (type: keyof typeof soundTypeMap) => {
    const storagePath = settings.customSounds?.[type];
    if (!storagePath) return '';
    
    const file = audioFiles.find(f => f.storage_path === storagePath);
    if (file) return file.file_name;
    
    const cachedFile = cachedFiles.find(f => f.name === storagePath);
    if (cachedFile) return cachedFile.name;
    
    return '';
  };

  const renderFileList = () => {
    const files = [
      ...audioFiles,
      ...cachedFiles.map(f => ({ 
        id: f.name, 
        file_name: f.name, 
        storage_path: f.name,
        isCached: true 
      }))
    ];
    
    if (files.length === 0) return null;

    return (
      <div className="space-y-2">
        {files.map((file) => (
          <div
            key={file.id}
            className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800 rounded-lg"
          >
            <span className="text-sm text-gray-700 dark:text-gray-300 truncate flex-1">
              {file.file_name}
            </span>
            <div className="flex items-center gap-2">
              <button
                onClick={() => handlePlay(file)}
                className="p-1 text-gray-400 hover:text-indigo-500 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700"
              >
                {playingId === file.id ? (
                  <Pause className="w-4 h-4" />
                ) : (
                  <Play className="w-4 h-4" />
                )}
              </button>
              
              {deletingId === file.id ? (
                <div className="flex items-center gap-2 bg-red-50 dark:bg-red-900/20 px-3 py-1 rounded-lg">
                  {isDeleting ? (
                    <div className="flex items-center gap-2">
                      <Loader2 className="h-4 w-4 animate-spin text-red-500" />
                      <span className="text-sm text-red-700 dark:text-red-400">Deleting...</span>
                    </div>
                  ) : (
                    <>
                      <AlertCircle className="h-4 w-4 text-red-500" />
                      <span className="text-sm text-red-700 dark:text-red-400">Delete?</span>
                      <button
                        onClick={() => handleDelete(file)}
                        className="text-red-700 dark:text-red-400 hover:text-red-800 dark:hover:text-red-300 text-sm font-medium"
                      >
                        Yes
                      </button>
                      <button
                        onClick={() => setDeletingId(null)}
                        className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300 text-sm"
                      >
                        No
                      </button>
                    </>
                  )}
                </div>
              ) : (
                <button
                  onClick={() => setDeletingId(file.id)}
                  className="p-1 text-gray-400 hover:text-red-500 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              )}
            </div>
          </div>
        ))}
      </div>
    );
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between mb-4">
        <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Sound Effects</label>
        <Switch.Root
          checked={soundEnabled}
          onCheckedChange={handleSoundToggle}
          className="w-11 h-6 bg-gray-200 dark:bg-gray-700 rounded-full relative data-[state=checked]:bg-indigo-600 dark:data-[state=checked]:bg-indigo-500 transition-colors"
        >
          <Switch.Thumb className="block w-4 h-4 bg-white dark:bg-gray-200 rounded-full shadow-lg transition-transform duration-100 translate-x-1 data-[state=checked]:translate-x-6" />
        </Switch.Root>
      </div>

      {soundEnabled && (
        <div className="flex items-center justify-between">
          <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Built-in Sound</label>
          <div className="flex rounded-md border border-gray-300 dark:border-gray-600 overflow-hidden">
            {(['smooth', 'legacy'] as const).map((style) => (
              <button
                key={style}
                type="button"
                onClick={() => updateSettings({ soundStyle: style })}
                className={`px-3 py-1.5 text-xs font-medium transition-colors ${
                  settings.soundStyle === style
                    ? 'bg-indigo-600 text-white'
                    : 'bg-white dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600'
                }`}
              >
                {style === 'smooth' ? 'Smooth' : 'Legacy'}
              </button>
            ))}
          </div>
        </div>
      )}

      {soundEnabled && !isPremium && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 dark:border-amber-800 dark:bg-amber-900/20 p-4">
          <p className="text-sm text-amber-800 dark:text-amber-200 mb-3">
            Custom sounds and cloud audio sync are premium features.
          </p>
          <button
            type="button"
            onClick={() => setShowUpgradeModal(true)}
            className="text-sm font-medium text-indigo-600 hover:text-indigo-700 dark:text-indigo-400 dark:hover:text-indigo-300"
          >
            Upgrade to Premium
          </button>
        </div>
      )}

      {soundEnabled && isPremium && (
        <div className="space-y-4">
          {[
            { type: 'focus', label: 'Focus' },
            { type: 'shortBreak', label: 'Short Break' },
            { type: 'longBreak', label: 'Long Break' },
          ].map(({ type, label }) => (
            <div key={type} className="space-y-1">
              <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                {label} Sound
              </label>
              <select
                value={getSelectedFileName(type as keyof typeof soundTypeMap)}
                onChange={(e) => {
                  const file = [...audioFiles, ...cachedFiles.map(f => ({ id: f.name, file_name: f.name, storage_path: f.name }))]
                    .find(f => f.file_name === e.target.value);
                  handleSoundSelect(type as keyof typeof soundTypeMap, file || null);
                }}
                className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
              >
                <option value="">Default Sound</option>
                {[...audioFiles, ...cachedFiles.map(f => ({ id: f.name, file_name: f.name, storage_path: f.name }))]
                  .map((file) => (
                    <option key={file.id} value={file.file_name}>
                      {file.file_name}
                    </option>
                  ))}
              </select>
            </div>
          ))}

          <Collapsible.Root
            open={isManageOpen}
            onOpenChange={setIsManageOpen}
            className="mt-6 border-t border-gray-200 dark:border-gray-700 pt-4"
          >
            <Collapsible.Trigger className="flex items-center justify-between w-full text-sm font-medium text-gray-900 dark:text-white">
              Manage Audio
              <ChevronDown
                className={`w-5 h-5 transition-transform ${
                  isManageOpen ? 'transform rotate-180' : ''
                }`}
              />
            </Collapsible.Trigger>
            
            <Collapsible.Content className="mt-4 space-y-4">
              {renderFileList()}
              <AudioUploader onUploadComplete={() => loadAudioFiles()} />
            </Collapsible.Content>
          </Collapsible.Root>
        </div>
      )}
    </div>
  );
}
